import { Amplify } from "@aws-amplify/core";
import * as Sentry from "@sentry/browser";
import { excludeGraphQLFetch } from "apollo-link-sentry";
import mixpanel from "mixpanel-browser";
import React from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  createRoutesFromElements,
  NavigateOptions,
  Route,
  RouterProvider,
  To,
} from "react-router-dom";
import { FlagsProvider } from "react-unleash-flags";

import { RemoveReason, ToastData, ToastProvider } from "./../../shared/components/design-system/Toaster/context";
import AppRouter from "./App";
import ApolloProvider from "./components/global-furniture/ApolloProvider";
import ErrorReporting from "./components/global-furniture/ErrorReporting";
import "./styles/styles.scss";

const flagConfig = {
  appName: import.meta.env.REACT_APP_GITLAB_ENVIRONMENT ?? "development",
  host: "https://gitlab.com/api/v4/feature_flags/unleash/12602311",
  instanceId: "tz5ma-BHLsf1Yu5-r7QY",
};

declare module "react-router" {
  interface NavigateFunction {
    (to: To, options?: NavigateOptions): void;
    (delta: number): void;
  }
}

export default function main() {
  Amplify.configure({
    Auth: {
      region: import.meta.env.REACT_APP_COGNITO_REGION,

      userPoolId: import.meta.env.REACT_APP_COGNITO_USER_POOL_ID,
      userPoolWebClientId: import.meta.env.REACT_APP_COGNITO_CLIENT_ID,

      authenticationFlowType: import.meta.env.REACT_APP_COGNITO_LOCAL ? "USER_PASSWORD_AUTH" : "USER_SRP_AUTH",
      endpoint: import.meta.env.REACT_APP_COGNITO_LOCAL,
      oauth: {
        domain: import.meta.env.REACT_APP_COGNITO_DOMAIN,
        redirectSignIn: import.meta.env.REACT_APP_DOMAIN,
        redirectSignOut: import.meta.env.REACT_APP_DOMAIN,
        responseType: "code",
      },
    },
  });

  if (import.meta.env.REACT_APP_SENTRY_ENVIRONMENT) {
    Sentry.init({
      dsn: "https://42df9006791ee87710960f530539fdcb@o4509785257148416.ingest.de.sentry.io/4509785298960464",
      beforeBreadcrumb: excludeGraphQLFetch,
      ignoreErrors: [
        "Authentication has expired",
        "No current user — logging out",
        "Refresh Token has expired",
        "Failed to fetch dynamically imported module",
        "No translation found for key",
        "Maximum call stack size exceeded",
        "error loading dynamically imported module",
        "Failed to execute 'sendBeacon' on 'Navigator'",
        "Object captured as promise rejection with keys",
        "Only empty categories can be deleted",
        "Invalid answer",
        "This user already exists",
        "User not assigned to this report",
        "The following users already exist",
        "xbrowser is not defined",
        "This session belongs to another user",
        "You cannot split by a code for which you have no questions",
        "Team is not assigned to this report",
        "Only deactivated users can have a blank email address",
        "You cannot edit an archived report",
        "Only closed reports can be re-opened",
        "Error invoking isAllowAutoFillOnDomain: Java object is gone",
        "swbrowser.inNightMode is not a function",
        "Non-Error promise rejection captured with value: Not implemented on this platform",
        "MediaPlaybackHandler.automaticVideoDownloadPopupEnabled is not a function",
      ],
      beforeSend(event) {
        const message = event.exception?.values?.[0]?.value ?? event.message;
        if (
          message === "$c" ||
          message === "Yc" ||
          message === "Oa" ||
          message === "fd" ||
          message === "od" ||
          message === "ud"
        ) {
          return null;
        }
        if (message === "Failed to fetch") {
          return null;
        }
        if (message === "Response not successful") {
          return null;
        }
        if (message === "Validation error") {
          return null;
        }
        if (message?.includes("Signature expired")) {
          return null;
        }
        if (message?.startsWith("Cannot read properties of undefined")) {
          return null;
        }
        if (message?.includes("Unexpected token '<'")) {
          return null;
        }
        return event;
      },
      release: import.meta.env.REACT_APP_SENTRY_RELEASE!,
      environment: import.meta.env.REACT_APP_SENTRY_ENVIRONMENT!,
      maxValueLength: 500,
    });
  }

  mixpanel.init(import.meta.env.REACT_APP_MIXPANEL_TOKEN, {
    opt_out_tracking_by_default: false,
    secure_cookie: true,
    api_host: "https://api-eu.mixpanel.com",
    // Uncomment this locally to enable Mixpanel debugging. Do not commit to source control with this line present as it creates large amounts of noise in the browser console and makes development more difficult:
    // debug: import.meta.env.REACT_APP_SENTRY_ENVIRONMENT !== "prod",
  });
  const basename = new URL(import.meta.env.REACT_APP_DOMAIN as string).pathname;
  const router = createBrowserRouter(createRoutesFromElements(<Route path="*" element={<RootComponent />}></Route>), {
    basename,
  });

  createRoot(document.getElementById("root")!).render(<RouterProvider router={router} />);
}

function onDismissToast(toast: ToastData, reason: RemoveReason) {
  if (reason === RemoveReason.TIMEOUT) return;
  mixpanel.track("Dismissed a toast", {
    reason: reason === RemoveReason.MANUAL ? "user" : "overflow",
    variant: toast.options.variant,
  });
}

const RootComponent = () => (
  <ErrorReporting>
    <FlagsProvider config={flagConfig}>
      <ToastProvider onDismiss={onDismissToast}>
        <ApolloProvider>
          <AppRouter />
        </ApolloProvider>
      </ToastProvider>
    </FlagsProvider>
  </ErrorReporting>
);
